import './custom.css';

import { useAtomValue, useSetAtom } from 'jotai';
import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';

import { createTheme, ThemeProvider } from '@mui/material/styles';

import { ErrorPage } from './components/ErrorPage';
import { Documents } from './components/Documents';
import { Layout } from './components/Layout';
import { LoadingSpinner } from './components/LoadingSpinner';
import { Login } from './components/Login';
import { Home } from './components/Home';
import { userNameAtom, selectedStateAtom } from './utils/AtomConfig';
import { AuthService } from './utils/AuthService';
import Constants from './utils/Constants';
import { SharePointService } from './utils/SharePointService';

export function App() {
  const authService = AuthService.Instance;
  const sharePointService = SharePointService.Instance;
  const history = useHistory();
  const theme = createTheme({
    palette: {
      primary: {
        main: '#003591',
      },
    },
  });

  const setUserName = useSetAtom(userNameAtom);
  const selectedState = useAtomValue(selectedStateAtom);

  const { isLoading: isInitializing, isSuccess: isInitialized } = useQuery(
    'authInitialize',
    () => authService.initialize(),
    {
      ...Constants.BASE_DISABLE_REFETCH_SETTINGS,
      staleTime: Infinity,
    }
  );

  const {
    data: userName,
    isLoading: isUserLoading,
    isSuccess,
  } = useQuery(
    Constants.QUERY_KEYS.USER_NAME,
    () => authService.getCurrentUserName(),
    {
      ...Constants.BASE_DISABLE_REFETCH_SETTINGS,
      enabled: isInitialized,
    }
  );

  const isAuthenticated = isSuccess && !!userName;
  const isLoading = isInitializing || isUserLoading;

  useEffect(() => {
    sharePointService.setNavigate((to, options) => {
      history.push(to, options?.state);
    });
  }, [history, sharePointService]);

  useEffect(() => {
    if (isAuthenticated && !!userName) {
      setUserName(userName);
      authService.checkExpiration();
    }
  }, [isAuthenticated, userName, setUserName, authService]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <ThemeProvider theme={theme}>
      <Layout>
        <Switch>
          <Route exact path='/error' component={ErrorPage} />
          <Route exact path='/login'>
            {isAuthenticated ? <Redirect to='/' /> : <Login />}
          </Route>
          <Route exact path='/'>
            {isAuthenticated ? <Home /> : <Redirect to='/login' />}
          </Route>
          <Route exact path='/documents'>
            {isAuthenticated ? (
              selectedState ? (
                <Documents />
              ) : (
                <Redirect to='/' />
              )
            ) : (
              <Redirect to='/login' />
            )}
          </Route>
        </Switch>
      </Layout>
    </ThemeProvider>
  );
}
